import { errorParams } from '@iris/i18n';
import { ApiError, ApiTimeoutError, type AuthResponse } from '@iris/shared';

/** Pages a return URL must never point to: they would bounce back to themselves. */
const AUTH_PAGES = /^\/(login|signup|forgot-password|reset-password)(\/|\?|$)/;

/**
 * Return URL after login (T4 §2.1 "→ /login with return URL"), limited to a path of this app so a crafted link
 * cannot send the user to another site.
 */
export function safeReturnTo(value: string | null | undefined, fallback = '/'): string {
  if (!value || !value.startsWith('/') || value.startsWith('//') || value.startsWith('/\\')) return fallback;
  if ([...value].some((c) => c.charCodeAt(0) < 0x20 || c.charCodeAt(0) === 0x7f)) return fallback;
  if (AUTH_PAGES.test(value)) return fallback;
  return value;
}

const PASSWORD_FIELDS = new Set(['password', 'newPassword']);

/**
 * `VALIDATION_FAILED.details.fields` (zod issue codes or password problems from the API) → message key shown
 * under the field (T4 §2.3, §2.4).
 */
export function fieldErrorKey(field: string, code: string): string {
  if (field === 'email') return 'auth.fieldErrors.emailInvalid';
  if (PASSWORD_FIELDS.has(field)) {
    if (code === 'TOO_SHORT' || code === 'too_small') return 'auth.fieldErrors.passwordTooShort';
    if (code === 'TOO_LONG' || code === 'too_big') return 'auth.fieldErrors.passwordTooLong';
    if (code === 'SAME_AS_EMAIL') return 'auth.fieldErrors.passwordSameAsEmail';
    if (code === 'TOO_COMMON') return 'auth.fieldErrors.passwordTooCommon';
  }
  if (field === 'firstName') return 'auth.fieldErrors.firstNameInvalid';
  return 'auth.fieldErrors.invalid';
}

export interface ErrorDescription {
  /** i18n key */
  key: string;
  params: Record<string, unknown>;
  /** shown under the message for support (5xx / unknown) */
  requestId: string | null;
}

/** T4 §2.4: registry code → its translation; network → "check your network"; anything else → generic + request id. */
export function describeError(error: unknown): ErrorDescription {
  if (error instanceof ApiTimeoutError || error instanceof TypeError) {
    return { key: 'common.networkError', params: {}, requestId: null };
  }
  if (error instanceof ApiError && error.status < 500 && error.code !== 'INTERNAL') {
    return { key: `errors.${error.code}`, params: errorParams(error.code, error.details), requestId: null };
  }
  const requestId = error instanceof ApiError ? error.requestId : null;
  return { key: 'common.unexpectedError', params: {}, requestId };
}

/**
 * After a failed submit: field errors of VALIDATION_FAILED go under their fields (T4 §2.3) and the function
 * returns null; anything else returns the form-level message.
 */
export function applyServerError(
  error: unknown,
  fields: readonly string[],
  setFieldError: (field: string, messageKey: string) => void,
): ErrorDescription | null {
  if (error instanceof ApiError && error.code === 'VALIDATION_FAILED') {
    const reported = (error.details as { fields?: Record<string, string> }).fields ?? {};
    const known = Object.entries(reported).filter(([field]) => fields.includes(field));
    for (const [field, code] of known) setFieldError(field, fieldErrorKey(field, code));
    if (known.length > 0 && known.length === Object.keys(reported).length) return null;
  }
  return describeError(error);
}

export type AuthEvent = { type: 'session'; session: AuthResponse } | { type: 'lost' };

/**
 * Connects the API client (created at module load) to the React auth state (created at render): the client
 * reports refreshed sessions and lost sessions, the AuthProvider listens.
 */
export function createAuthBridge() {
  const listeners = new Set<(event: AuthEvent) => void>();
  const emit = (event: AuthEvent) => {
    for (const listener of listeners) listener(event);
  };
  return {
    onSession: (session: AuthResponse) => emit({ type: 'session', session }),
    onAuthLost: () => emit({ type: 'lost' }),
    subscribe(listener: (event: AuthEvent) => void): () => void {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}

export type AuthBridge = ReturnType<typeof createAuthBridge>;
